import { useCallback, useEffect, useMemo, useState } from "react";
import { SlidersHorizontal, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import useAdminAutoRefresh from "../../lib/useAdminAutoRefresh";
import DepositBonusControl from "../../components/DepositBonusControl";

const RULE_DEFAULTS = {
  min_load_multiplier: 4,
  max_load_multiplier: 10,
  min_redeem: 20,
  max_redeem: 1000,
  redeem_fee_percent: 5,
  first_deposit_bonus_percent: 100,
  regular_deposit_bonus_percent: 20,
};

const EMPTY = {
  name: "",
  category: "slots",
  description: "",
  image_url: "",
  play_url: "",
  rtp: 95,
  min_bet: 10,
  max_bet: 1000,
  status: "live",
  ...RULE_DEFAULTS,
};

const numericKeys = [
  "rtp",
  "min_bet",
  "max_bet",
  "min_load_multiplier",
  "max_load_multiplier",
  "min_redeem",
  "max_redeem",
  "redeem_fee_percent",
  "first_deposit_bonus_percent",
  "regular_deposit_bonus_percent",
];

function withDefaults(game = {}) {
  return { ...EMPTY, ...game };
}

function gamePayload(game) {
  const payload = withDefaults(game);
  numericKeys.forEach((key) => {
    const fallback = EMPTY[key];
    const value = Number(payload[key]);
    payload[key] = Number.isFinite(value) ? value : fallback;
  });
  payload.category = payload.category || "slots";
  payload.status = payload.status || "live";
  return payload;
}

export default function AdminGames() {
  const [games, setGames] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busyId, setBusyId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [bonusSaving, setBonusSaving] = useState(false);

  const sortedGames = useMemo(
    () => [...games].sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))),
    [games],
  );

  const load = useCallback(() => api.get("/games?status_filter=all").then(({ data }) => setGames(Array.isArray(data) ? data : [])), []);

  useEffect(() => {
    load().catch((e) => toast.error(formatApiError(e)));
  }, [load]);
  useAdminAutoRefresh(() => load(), { enabled: !editing && !saving && !bonusSaving });

  const openSettings = (game) => {
    const normalized = withDefaults(game);
    setEditing(normalized);
    setForm(normalized);
  };

  const closeSettings = () => {
    if (saving) return;
    setEditing(null);
  };

  const updateForm = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const saveSettings = async (e) => {
    e.preventDefault();
    if (!editing) return;
    setSaving(true);
    try {
      const payload = gamePayload({ ...editing, ...form });
      await api.put(`/admin/games/${editing.id}`, payload);
      toast.success("Game settings saved");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleGame = async (game) => {
    const normalized = withDefaults(game);
    const nextStatus = normalized.status === "live" ? "maintenance" : "live";
    setBusyId(normalized.id);
    try {
      await api.put(`/admin/games/${normalized.id}`, gamePayload({ ...normalized, status: nextStatus }));
      toast.success(`${normalized.name} ${nextStatus === "live" ? "enabled" : "disabled"}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-3 sm:p-6 md:p-8">
      <DepositBonusControl games={games} onApplied={load} onSavingChange={setBonusSaving}
        disabled={Boolean(editing || saving || busyId)} />
      <div className="overflow-hidden rounded-lg border border-[#332a10] bg-[#101010]">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[940px] table-fixed text-sm">
            <thead className="bg-[#171719] text-left text-xs uppercase tracking-wider text-slate-400">
              <tr>
                <th className="w-[34%] px-4 py-4 sm:px-6">Game</th>
                <th className="w-[17%] px-4 py-4">Category</th>
                <th className="w-[20%] px-4 py-4">Source</th>
                <th className="w-[15%] px-4 py-4 text-center">Settings</th>
                <th className="w-[14%] px-4 py-4 text-right">Enabled</th>
              </tr>
            </thead>
            <tbody>
              {sortedGames.map((raw) => {
                const game = withDefaults(raw);
                const enabled = game.status === "live";
                return (
                  <tr
                    key={game.id}
                    data-testid={`admin-game-row-${game.id}`}
                    className="border-t border-[#332a10]/80 transition hover:bg-white/[0.025]"
                  >
                    <td className="px-4 py-4 sm:px-6">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="grid h-11 w-11 shrink-0 place-items-center rounded bg-[#221805] text-xs font-black uppercase tracking-wide text-[#F6C90E]">
                          {gameInitials(game.name)}
                        </div>
                        <div className="min-w-0">
                          <div className="truncate text-base font-semibold text-white">{game.name || "Untitled game"}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-slate-300">{titleize(game.category)}</td>
                    <td className="px-4 py-4">
                      <span className="inline-flex rounded-full border border-[#5b4a17] bg-black/20 px-3 py-1 text-xs font-semibold text-white">
                        {sourceLabel(game)}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <button
                        type="button"
                        onClick={() => openSettings(game)}
                        disabled={bonusSaving}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-full text-slate-200 transition hover:bg-white/10 hover:text-white"
                        title={`Settings for ${game.name}`}
                        aria-label={`Settings for ${game.name}`}
                        data-testid={`edit-game-${game.id}`}
                      >
                        <SlidersHorizontal className="h-4 w-4" />
                      </button>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <Switch
                        checked={enabled}
                        disabled={bonusSaving || busyId === game.id}
                        label={`${enabled ? "Disable" : "Enable"} ${game.name}`}
                        onClick={() => toggleGame(game)}
                      />
                    </td>
                  </tr>
                );
              })}
              {sortedGames.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-slate-500">
                    No games found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/75 p-4 backdrop-blur-sm" onClick={closeSettings}>
          <form
            onSubmit={saveSettings}
            onClick={(e) => e.stopPropagation()}
            className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-white/10 bg-[#101010] shadow-2xl"
            data-testid="game-dialog"
          >
            <div className="flex shrink-0 items-center justify-between gap-4 border-b border-white/10 px-5 py-5 sm:px-6">
              <div className="flex min-w-0 items-center gap-4">
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded bg-[#221805] text-xs font-black uppercase tracking-wide text-[#F6C90E]">
                  {gameInitials(editing.name)}
                </div>
                <div className="min-w-0">
                  <h3 className="truncate text-xl font-bold text-white">{editing.name || "Game settings"}</h3>
                  <p className="mt-1 text-sm text-slate-400">Game rules apply to loads, redeems, and bonus calculations.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeSettings}
                className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-slate-300 hover:bg-white/10 hover:text-white"
                aria-label="Close settings"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-6 sm:px-6">
              <div className="mb-7 rounded-3xl bg-white/[0.055] px-4 py-4 text-sm text-slate-400 sm:px-5">
                <div className="font-semibold text-slate-300">Admin defaults for this game:</div>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <div>
                    First-time bonus: <span className="font-bold text-slate-200">{numberOrDefault(form.first_deposit_bonus_percent, 100)}%</span>
                  </div>
                  <div>
                    Regular bonus: <span className="font-bold text-slate-200">{numberOrDefault(form.regular_deposit_bonus_percent, 20)}%</span>
                  </div>
                </div>
                <p className="mt-2">
                  Update values below to control this game's minimums, maximums, cash-out fee, and deposit bonuses.
                </p>
              </div>

              <div className="grid gap-x-4 gap-y-6 sm:grid-cols-2">
                <Field label="Min load multiplier (X)">
                  <input
                    type="number"
                    step="0.1"
                    value={form.min_load_multiplier}
                    onChange={(e) => updateForm("min_load_multiplier", e.target.value)}
                    className={inputClass}
                  />
                </Field>
                <Field label="Max load multiplier (X)">
                  <input
                    type="number"
                    step="0.1"
                    value={form.max_load_multiplier}
                    onChange={(e) => updateForm("max_load_multiplier", e.target.value)}
                    className={inputClass}
                  />
                </Field>
                <Field label="Min redeem ($)">
                  <input
                    type="number"
                    value={form.min_redeem}
                    onChange={(e) => updateForm("min_redeem", e.target.value)}
                    className={inputClass}
                  />
                </Field>
                <Field label="Max redeem ($)">
                  <input
                    type="number"
                    value={form.max_redeem}
                    onChange={(e) => updateForm("max_redeem", e.target.value)}
                    className={inputClass}
                  />
                </Field>
                <Field label="Redeem fee (%)">
                  <input
                    type="number"
                    step="0.1"
                    value={form.redeem_fee_percent}
                    onChange={(e) => updateForm("redeem_fee_percent", e.target.value)}
                    className={inputClass}
                  />
                </Field>
                <div className="hidden sm:block" />
                <Field label="First-time deposit bonus (%)">
                  <input
                    type="number"
                    value={form.first_deposit_bonus_percent}
                    onChange={(e) => updateForm("first_deposit_bonus_percent", e.target.value)}
                    className={inputClass}
                  />
                </Field>
                <Field label="Regular deposit bonus (%)">
                  <input
                    type="number"
                    value={form.regular_deposit_bonus_percent}
                    onChange={(e) => updateForm("regular_deposit_bonus_percent", e.target.value)}
                    className={inputClass}
                  />
                </Field>
              </div>
            </div>

            <div className="flex shrink-0 justify-end gap-3 border-t border-white/10 px-5 py-4 sm:px-6">
              <button
                type="button"
                onClick={closeSettings}
                disabled={saving}
                className="rounded-full px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/5 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                data-testid="game-save"
                className="rounded-full bg-[#F6C90E] px-6 py-2.5 text-sm font-bold text-black hover:bg-[#ffd84a] disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save settings"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const inputClass = "h-12 w-full rounded-full border border-white/10 bg-white/[0.055] px-4 text-base font-semibold text-white outline-none transition placeholder:text-slate-600 focus:border-[#F6C90E]/60 focus:bg-white/[0.075]";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs font-black uppercase tracking-wider text-slate-400">{label}</span>
      {children}
    </label>
  );
}

function Switch({ checked, disabled, label, onClick }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={`relative inline-flex h-6 w-12 shrink-0 items-center rounded-full p-0.5 transition disabled:cursor-wait disabled:opacity-60 ${
        checked ? "bg-[#F6C90E]" : "bg-slate-700"
      }`}
    >
      <span
        className={`h-5 w-5 rounded-full bg-[#050505] shadow transition-transform ${
          checked ? "translate-x-6" : "translate-x-0"
        }`}
      />
    </button>
  );
}

function gameInitials(name = "") {
  const words = String(name || "")
    .replace(/[^a-z0-9 ]/gi, " ")
    .split(" ")
    .filter(Boolean);
  if (words.length >= 2) return `${words[0][0]}${words[1][0]}`.toUpperCase();
  return String(name || "G").slice(0, 2).toUpperCase();
}

function titleize(value) {
  return String(value || "Gaming")
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function sourceLabel(game) {
  return titleize(game.source || game.provider || "Admin");
}

function numberOrDefault(value, fallback) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}
